import { useEffect, useRef, useState } from 'react';
import { UMIcon, UMR, UMStack } from '../_components';

interface FileState { name?: string; error?: string; loading?: boolean; failed?: string[] }

/** Identifiants fictifs rejetés par l'import simulé. */
export const MOCK_FAILED_BUILDINGS = ['BFR464728784', 'BFR36774667483', 'BFR920720145', 'BFR920720233', 'BFR75108004912', 'BFR69123001877', 'BFR13055002310', 'BFR33063004125'];
export const FAILED_PREVIEW_LIMIT = 5;
const LOAD_DELAY_MS = 1800;

export function useBuildingUpload() {
  const [files, setFiles] = useState<Record<string, FileState>>({});
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);
  const choose = (selectionId: string, fileName: string | undefined) => {
    if (fileName === undefined) return;
    if (!/\.(xlsx|xls)$/i.test(fileName)) {
      setFiles((prev) => ({ ...prev, [selectionId]: { ...prev[selectionId], error: 'Format invalide. Choisissez un fichier Excel (.xlsx ou .xls).' } }));
      return;
    }
    clearTimeout(timers.current[selectionId]);
    setFiles((prev) => ({ ...prev, [selectionId]: { name: fileName, loading: true } }));
    timers.current[selectionId] = setTimeout(() => {
      setFiles((prev) => ({ ...prev, [selectionId]: { name: fileName, failed: MOCK_FAILED_BUILDINGS } }));
    }, LOAD_DELAY_MS);
  };
  return { files, choose };
}

export function BuildingUpload({ state, onChoose, disabled }: {
  state?: FileState; onChoose: (name: string | undefined) => void; disabled: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  return <UMStack>
    <button type="button" disabled={disabled} onClick={() => input.current?.click()} style={{
      width: '100%', height: 40, boxSizing: 'border-box', padding: '0 16px',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      background: UMR.white, border: `1px solid ${UMR.blue}`, borderRadius: 0, color: UMR.blue,
      fontFamily: UMR.font, fontSize: 14, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1,
    }}>
      <UMIcon name="add_circle" size={20} color={UMR.blue} />
      Charger un fichier
    </button>
    {/* Native file picker only: Fluid/UM has no file-picker equivalent. No file content is read. */}
    <input ref={input} type="file" accept=".xlsx,.xls" disabled={disabled}
      aria-label="Fichier Excel des bâtiments" style={{ display: 'none' }}
      onChange={(event) => {
        const name = event.target.files?.[0]?.name;
        event.target.value = '';
        if (!disabled) onChoose(name);
      }} />
    {/* Même format que le commentaire des centrales de production : 12px, texte atténué, sous les actions. */}
    <div style={{ fontSize: 12, color: UMR.textMuted }}>
      Vous pouvez ajouter des bâtiments via la carte ou fichier excel.
    </div>
    <div role="status" aria-live="polite" style={{ fontSize: 12, color: UMR.textMuted, overflowWrap: 'anywhere' }}>
      {state?.name && <div>Fichier : {state.name}</div>}
      {state?.loading && <div>Chargement du fichier…</div>}
      {state?.error && <div role="alert">{state.error}</div>}
    </div>
    {state?.failed && <FailedBuildings ids={state.failed} />}
  </UMStack>;
}

function FailedBuildings({ ids }: { ids: string[] }) {
  const [expanded, setExpanded] = useState(false);
  const hidden = ids.length - FAILED_PREVIEW_LIMIT;
  const shown = expanded ? ids : ids.slice(0, FAILED_PREVIEW_LIMIT);
  return <div role="alert" style={{
    display: 'flex', flexDirection: 'column', gap: 4, padding: '8px 12px', fontSize: 12, lineHeight: '18px',
    color: UMR.textStrong, background: UMR.grey100, border: `1px solid ${UMR.border}`, borderLeft: `4px solid ${UMR.warning}`,
  }}>
    <div style={{ fontWeight: 600 }}>Les bâtiments suivants n’ont pas pu être importés :</div>
    <div aria-label="Bâtiments non importés" style={{ overflowWrap: 'anywhere' }}>{shown.join(', ')}{!expanded && hidden > 0 && '…'}</div>
    {hidden > 0 && <button type="button" aria-expanded={expanded} onClick={() => setExpanded((v) => !v)} style={{
      alignSelf: 'flex-start', border: 'none', background: 'transparent', padding: 0, cursor: 'pointer',
      fontFamily: UMR.font, fontSize: 12, color: UMR.blueLink, textDecoration: 'underline',
    }}>{expanded ? 'Voir moins' : `Voir plus (${hidden})`}</button>}
  </div>;
}
